import React, { memo as Memo, useMemo, useState, useRef, useCallback, useEffect } from 'react'
import { useRouter } from 'next/router'

//* HOC's
import { withDataContext, withUIContext, withLanguageContext } from 'context'

//* Components
import Container from 'components/common/Container'
import Icon from 'components/common/Icon'
import LottieAnimation from 'components/common/LottieAnimation'
import MenuItems from 'components/common/MenuItems'
import Text from 'components/common/Text'
import CustomLink from 'components/common/CustomLink'
import Line from 'components/common/Line'
import AnimationWrap from 'components/common/AnimationWrap'

//* Styles
import Footer from './style'

//* Lottie
import footerSlotMachine from 'lottieAnimation/footerSlotMachine.json'

//* Config
import config from 'helpers/_config'

//* Helpers
import { slideDown, changeWidth } from 'helpers/_gsapFunctions'
import { gsap } from 'gsap'

const AppFooter = Memo(({ winWidth, global, selectedLang, ...props }) => {
	const globalData = useMemo(() => global?.[selectedLang], [global, selectedLang])

	//! Router
	const router = useRouter()

	//! States
	const [secObj, setSecObj] = useState()

	//! Refs
	const items = useRef([])
	const lineRef = useRef()
	const titleRef = useRef()
	const descriptionRef = useRef()
	const slotRef = useRef()
	const youtubeRef = useRef()
	const facebookRef = useRef()
	const instagramRef = useRef()
	const tiktokRef = useRef()
	const twitterRef = useRef()
	const privacyRef = useRef()
	const termsRef = useRef()
	const conceptRef = useRef()
	const copyrightRef = useRef()

	useEffect(() => {
		if (items?.current) {
			setSecObj([items.current])
		}
	}, [items])

	const startAnimeArr = useCallback((x) => {
		if (x) {
			const itemsArr = [
				slotRef.current,
				titleRef.current,
				descriptionRef.current,
				youtubeRef.current,
				facebookRef.current,
				instagramRef.current,
				tiktokRef.current,
				twitterRef.current,
				privacyRef.current,
				termsRef.current,
				conceptRef.current,
				copyrightRef.current,
			]

			gsap.set(lineRef.current, {
				width: '0%',
			})

			gsap.set(itemsArr, {
				y: '200%',
			})

			//! changeWidth(item)
			changeWidth(lineRef.current)
			//! slideDown(item, stagger, delay)
			slideDown(itemsArr, 0.2)
		}
	}, [])

	return globalData ? (
		<Footer>
			<AnimationWrap state={secObj} startAnimeArr={startAnimeArr} dependency={router.asPath}>
				<Container ref={items}>
					<div ref={lineRef} className='animStartStateWidth'>
						<Line color='orange' />
					</div>
					<div className='overflowHidden'>
						<Text tag={'h1'} ref={titleRef} className={'translate-200 terminaBold footerTitle'} text='NG slot' />
					</div>
					<div className='FlexBox descriptionAndRouteBlock'>
						<div className='overflowHidden'>
							<Text ref={descriptionRef} className='h10 footerDescription translate-200 maisonMedium' text='footerDescription' />
						</div>
						{winWidth >= 1024 && <MenuItems type={'footer'} />}
						<div ref={slotRef} className='lottieMainWrap translate-200'>
							<LottieAnimation animData={footerSlotMachine} autoplay={true} isPlay={true} loop={true} />
						</div>
					</div>
					{winWidth < 1024 && <MenuItems type={'footer'} />}
					<div className='socials FlexBox overflowHidden'>
						<a
							href={globalData.socials.youtube.link}
							ref={youtubeRef}
							className='socialLink orange h8 terminaBold cursorPointer translate-200'
							rel='noopener noreferrer'
							aria-label='youtube link'
							target='_blank'
						>
							<Icon className='icon-youTube' />
						</a>
						<a
							href={globalData.socials.facebook.link}
							ref={facebookRef}
							className='socialLink orange h8 terminaBold cursorPointer translate-200'
							rel='noopener noreferrer'
							aria-label='facebook link'
							target='_blank'
						>
							<Icon className='icon-facebook-other' />
						</a>
						<a
							href={globalData.socials.instagram.link}
							ref={instagramRef}
							className='socialLink orange h8 terminaBold cursorPointer translate-200'
							rel='noopener noreferrer'
							aria-label='instagram link'
							target='_blank'
						>
							<Icon className='icon-instagram-other' />
						</a>
						<a
							href={globalData.socials.tiktok.link}
							ref={tiktokRef}
							className='socialLink orange h8 terminaBold cursorPointer translate-200'
							rel='noopener noreferrer'
							aria-label='tiktok link'
							target='_blank'
						>
							<Icon className='icon-tikTok' />
						</a>
						<a
							href={globalData.socials.twitter.link}
							ref={twitterRef}
							className='socialLink orange h8 terminaBold cursorPointer translate-200'
							rel='noopener noreferrer'
							aria-label='twitter link'
							target='_blank'
						>
							<Icon className='icon-twitter' />
						</a>
					</div>
					<div className='privacyBlock FlexBox overflowHidden'>
						<div className='overflowHidden'>
							<CustomLink url={config.routes.policy.path} ref={privacyRef} className='privacyLink translate-200'>
								<Text text={'privacyPolicy'} className='h9 terminaRegular underline' />
							</CustomLink>
						</div>
						<CustomLink url={config.routes.terms.path} ref={termsRef} className='translate-200'>
							<Text text={'termsAndConditions'} className='h9 terminaRegular underline' />
						</CustomLink>
					</div>
					<div className='FlexBox copyright '>
						<div className='overflowHidden'>
							<div className='FlexBox conceptLinkWrap translate-200' ref={conceptRef}>
								<Text className='h9 orange terminaBold' text='createdBy' />
								<a href='https://conceptstudio.com/en/' className='h9 terminaBold space' target='_blank'>
									Concept Studio
								</a>
							</div>
						</div>
						<div className='overflowHidden'>
							<div className='FlexBox copyrightYear translate-200' ref={copyrightRef}>
								<Text className='h9 terminaBold' text={'©Copyright'} />
								<Text className='h9 terminaBold orange space' text={'NG'} />
								<Text className='h9 terminaBold space' text={new Date().getFullYear()} />
							</div>
						</div>
					</div>
				</Container>
			</AnimationWrap>
		</Footer>
	) : null
})

export default withLanguageContext(withUIContext(withDataContext(AppFooter, ['global']), ['winWidth']), ['selectedLang'])
